'use client'

import { memo } from 'react'
import { Button, HStack, Text, Wrap, WrapItem } from '@chakra-ui/react'
import type { MapLocatorTag } from 'ecosystem'
import { MAP_LOCATOR_CN, mapLocatorCn as cn } from './constants'
import { interpolateCount } from './utils'

interface FilterBarProps {
  tags: MapLocatorTag[]
  selectedTagIds: string[]
  onToggle: (tagId: string) => void
  onClear: () => void
  hasActiveFilters: boolean
  resultCount: number
  labels: {
    clearFilters: string
    resultsCount: string
  }
}

const FilterBar = ({
  tags,
  selectedTagIds,
  onToggle,
  onClear,
  hasActiveFilters,
  resultCount,
  labels
}: FilterBarProps) => {
  if (!tags.length && !hasActiveFilters) {
    return null
  }

  return (
    <HStack
      align="center"
      className={cn(MAP_LOCATOR_CN.FILTERS)}
      justify="space-between"
      spacing={4}
      w="full"
      wrap="wrap"
    >
      <Wrap spacing={2}>
        {tags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id)

          return (
            <WrapItem key={tag.id}>
              <Button
                aria-pressed={isSelected}
                borderColor={tag.color || 'gray.300'}
                borderWidth="1px"
                className={cn(MAP_LOCATOR_CN.FILTER_CHIP)}
                onClick={() => onToggle(tag.id)}
                rounded="full"
                size="sm"
                sx={
                  isSelected && tag.color
                    ? { bg: tag.color, color: 'white', _hover: { bg: tag.color } }
                    : undefined
                }
                variant={isSelected ? 'solid' : 'outline'}
              >
                {tag.label}
              </Button>
            </WrapItem>
          )
        })}
      </Wrap>

      <HStack spacing={3}>
        <Text color="gray.600" fontSize="sm">
          {interpolateCount(labels.resultsCount, resultCount)}
        </Text>

        {hasActiveFilters ? (
          <Button onClick={onClear} size="sm" variant="link" bg="transparent">
            {labels.clearFilters}
          </Button>
        ) : null}
      </HStack>
    </HStack>
  )
}

/* Memoised so a keystroke, which only changes state upstream, does not walk
 * this subtree before React has painted the character. */
export default memo(FilterBar)
